import { createContext, FunctionComponent, PropsWithChildren } from 'react';
import {
  Brand,
  BRAND_DISPLAY_NAME,
  BRAND_NAME,
  FormattingContext,
} from '../types';

type EnvironmentContextType = {
  directUploadsUrl: string;
  googleAuthUrl: string;
  patientGoogleAuthUrl: string;
  apiHost: string;
  redirectAPIHost: string;
  environment: string;
  brand: Brand;
  brandDisplayName: (typeof BRAND_DISPLAY_NAME)[Brand];
  formattingContext: FormattingContext;
};

type Props = Pick<
  EnvironmentContextType,
  'apiHost' | 'environment' | 'brand' | 'formattingContext'
>;

export const EnvironmentContext = createContext<EnvironmentContextType>({
  directUploadsUrl: '',
  googleAuthUrl: '',
  patientGoogleAuthUrl: '',
  apiHost: '',
  redirectAPIHost: '',
  environment: 'staging',
  brand: BRAND_NAME.Freddie,
  brandDisplayName: BRAND_DISPLAY_NAME[BRAND_NAME.Freddie],
  formattingContext: 'provider',
});

export const EnvironmentProvider: FunctionComponent<
  PropsWithChildren<Props>
> = ({ children, apiHost, environment, brand, formattingContext }) => {
  // NOTE: this is a prettyhack solution to making sure that the domains which we redirect to the
  // BE match the FE, such that the cookie jar containing the ajs_anonymous_id is shared across the domains.
  // this will allow the segment event in the FE and BE to use the same anonymous id.
  // wo do this hack locally because I'm too scared to change the full VITE_API_HOST environment variable across the board
  // because I don't know what other side-effects that might have.
  // These links came from https://docs.google.com/document/d/1VlecYxy1STFGuTgCHAOr04r5JlrAMpn-T4HaHLOlrG0/edit?tab=t.0
  let redirectAPIHost: string;
  if (environment === 'staging') {
    redirectAPIHost = 'https://start-staging.gofreddie.com';
  } else if (environment === 'production') {
    redirectAPIHost = 'https://start.gofreddie.com';
  } else {
    // set VITE_API_HOST in apps/freddie/.env
    redirectAPIHost = apiHost || 'http://localhost:3001';
  }

  return (
    <EnvironmentContext
      value={{
        directUploadsUrl: `${apiHost}/uploads/direct_uploads`,
        googleAuthUrl: `${apiHost}/auth/google_oauth2/callback`,
        patientGoogleAuthUrl: `${apiHost}/auth/google_oauth2/patient_callback`,
        apiHost,
        redirectAPIHost,
        environment,
        brand,
        brandDisplayName: BRAND_DISPLAY_NAME[brand],
        formattingContext: formattingContext,
      }}
    >
      {children}
    </EnvironmentContext>
  );
};
